import {
  Box,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { formatDateWithSlashes, formatTime } from "../../../utils";
import {
  caseResultColorMap,
  CaseResultType,
  CaseRevisionMeta,
  RelatedCaseRow,
  TableColumn as Column,
  TableSortConfig as SortConfig,
  TableSortDirection as SortDirection,
  CaseFile,
} from "../types";
import { Password, VisibilityOutlined } from "@mui/icons-material";

const columns: Column<CaseRevisionMeta>[] = [
  {
    id: "date",
    label: "Date",
    sortable: true,
    getValue: (row) => row.created_at,
  },
  {
    id: "actionTaken",
    label: "Action Taken",
    sortable: false,
    getValue: (row) => row.description,
  },
  {
    id: "outcomeImpact",
    label: "Outcome Impact",
    sortable: true,
    getValue: (row) => row.is_impact,
  },
  {
    id: "revisedBy",
    label: "Revised By",
    sortable: true,
    getValue: (row) => row.revised_by,
  },
  {
    id: "view",
    label: "",
    getValue: () => null,
  },
];

const RevisionsTable: React.FC<{
  revisions: CaseRevisionMeta[];
  onRowClick: (row: CaseRevisionMeta) => void;
}> = ({ revisions, onRowClick }) => {
  const [visibleData, setVisibleData] =
    React.useState<CaseRevisionMeta[]>(revisions);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "",
    direction: "asc",
  });

  useEffect(() => {
    setVisibleData(revisions);
  }, [revisions]);

  const renderCell = (row: CaseRevisionMeta, columnId: string) => {
    switch (columnId) {
      case "date":
        return (
          <TableCell>
            <Stack direction="row" spacing={1}>
              <Typography variant="caption" fontWeight={"bold"}>
                {formatDateWithSlashes(row.created_at)}
              </Typography>
              <Typography variant="caption">|</Typography>
              <Typography variant="caption" fontWeight={"light"}>
                {formatTime(row.created_at)}
              </Typography>
            </Stack>
          </TableCell>
        );
      case "actionTaken":
        return (
          <TableCell>
            <Typography fontWeight={"light"} variant="caption">
              {row.description}
            </Typography>
          </TableCell>
        );
      case "outcomeImpact":
        return (
          <TableCell>
            <Chip
              label={row.is_impact ? "yes" : "no"}
              style={{
                textTransform: "capitalize",
                backgroundColor: row.is_impact
                  ? "#ACF2C8"
                  : "rgba(239, 108, 0, 0.30))",
                color: row.is_impact ? "#266A4A" : "#E65100",
                borderRadius: 15,
              }}
            />
          </TableCell>
        );
      case "revisedBy":
        return (
          <TableCell>
            <Chip
              label={row.revised_by}
              style={{
                textTransform: "capitalize",
                backgroundColor: row.is_impact
                  ? "#ACF2C8"
                  : "rgba(239, 108, 0, 0.30))",
                color: row.is_impact ? "#266A4A" : "#E65100",
                borderRadius: 15,
              }}
            />
          </TableCell>
        );
      case "view":
        return (
          <TableCell>
            <IconButton onClick={() => onRowClick(row)}>
              <Stack sx={{ color: "#266A4A" }} direction="row" spacing={1}>
                <VisibilityOutlined fontSize="small"/>
                <Typography variant="caption">View</Typography>
              </Stack>
            </IconButton>
          </TableCell>
        );
    }
  };

  const handleSort = (columnId: string) => {
    setSortConfig((prevConfig) => ({
      key: columnId,
      direction:
        prevConfig.key === columnId && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const renderSortableHeader = (column: Column<CaseRevisionMeta>) => {
    if (!column.sortable) {
      return (
        <Typography variant="caption" color="#475467">
          {column.label}
        </Typography>
      );
    }

    return (
      <TableSortLabel
        active={sortConfig.key === column.id}
        direction={sortConfig.key === column.id ? sortConfig.direction : "asc"}
        onClick={() => handleSort(column.id)}
      >
        <Typography variant="caption" color="#475467">
          {column.label}
        </Typography>
      </TableSortLabel>
    );
  };

  return (
    <TableContainer sx={{ backgroundColor: "white", borderRadius: 3 }}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.id}>
                {renderSortableHeader(column)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleData.map((row, index) => (
            <TableRow key={index}>
              {columns.map((column) => (
                <React.Fragment key={column.id}>
                  {renderCell(row, column.id)}
                </React.Fragment>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RevisionsTable;
